/**
 * Le comptoir : la table, la lumière, la caméra et les trois dés.
 *
 * Extrait de l'écran de lancement pour servir aux deux modes — la partie et le bac à
 * sable. Une scène dupliquée serait deux scènes à régler, et le décor du 421 ne
 * change pas selon la raison pour laquelle on lance les dés.
 *
 * Tout ce qui touche au budget du § 7.3 vit ici : DPR plafonné, une seule ombre,
 * repli automatique si le fps s'écroule, moteur physique chargé en différé, et perte
 * du contexte WebGL rattrapée.
 */

import { ContactShadows, OrbitControls } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { PerspectiveCamera } from "three";

import { cadrerPiste, VUE, ZONE_VISIBLE } from "./cadrage.js";
import { DUREE_REGARD, focaleDuRegard, pivotDuRegard } from "./regard.js";
import { PLATEAU } from "./plateau.js";
import type { DeAffiche, DemandeDeJet } from "./DesQuiRoulent.js";
import type { DePose, Jet } from "./lancer.js";
import { Sonde, sondeActive } from "./Sonde.js";

const DesQuiRoulent = lazy(() => import("./DesQuiRoulent.js"));

/**
 * Ajuste le cadrage à la forme de l'écran.
 *
 * Sans cela, le cadrage réglé sur un écran de bureau laisse les dés sortir du cadre
 * en portrait de téléphone — deux fois plus étroit. `tests/cadrage.test.ts` vérifie
 * la couverture sur les formats qu'on promet de servir.
 */
function CadrageAdaptatif() {
  const camera = useThree((s) => s.camera);
  const taille = useThree((s) => s.size);

  useEffect(() => {
    if (!(camera instanceof PerspectiveCamera)) return;
    const aspect = taille.width / Math.max(1, taille.height);
    const cadrage = cadrerPiste(ZONE_VISIBLE, aspect);
    camera.position.copy(cadrage.position);
    camera.fov = cadrage.fov;
    camera.lookAt(cadrage.cible);
    camera.updateProjectionMatrix();
  }, [camera, taille.width, taille.height]);

  return null;
}

/**
 * Le regard qui se resserre sur le résultat.
 *
 * ⚠️ On ne touche QUE la focale et l'azimut des contrôles. `OrbitControls` écrit la
 * position de la caméra à chaque image depuis son propre état : la déplacer en même
 * temps que lui, c'est se battre avec lui, et le joueur qui fait tourner la scène du
 * doigt gagnerait un tremblement. La focale, elle, n'appartient à personne.
 *
 * La focale de base est relue à chaque regard plutôt que retenue une fois : le
 * cadrage s'ajuste à la forme de l'écran (`CadrageAdaptatif`), et une valeur figée
 * ferait sauter le cadrage d'un portrait à un paysage.
 */
function Regard({ jet, ampleur }: {
  readonly jet: number; readonly ampleur: number;
}) {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as
    { getAzimuthalAngle?: () => number; setAzimuthalAngle?: (a: number) => void } | null;
  const debut = useRef<number | null>(null);
  const base = useRef({ fov: 0, azimut: 0 });

  useEffect(() => {
    if (jet <= 0 || !(camera instanceof PerspectiveCamera)) return;
    base.current = {
      fov: camera.fov,
      azimut: controls?.getAzimuthalAngle?.() ?? 0,
    };
    debut.current = 0;
  }, [jet, camera, controls]);

  useFrame((_, delta) => {
    if (debut.current === null || !(camera instanceof PerspectiveCamera)) return;
    // Le delta est plafonné : au retour d'arrière-plan il vaut plusieurs secondes,
    // et le regard sauterait d'un coup à sa fin — même précaution que pour les dés.
    debut.current += Math.min(delta, 0.05);
    const ecoule = debut.current;

    camera.fov = focaleDuRegard(base.current.fov, ecoule, ampleur);
    camera.updateProjectionMatrix();
    controls?.setAzimuthalAngle?.(
      base.current.azimut + pivotDuRegard(ecoule, ampleur));

    if (ecoule >= DUREE_REGARD) {
      // On remet exactement les valeurs de départ : une remise « à peu près »
      // dérive d'un jet à l'autre, et au bout de dix manches on joue de travers.
      camera.fov = base.current.fov;
      camera.updateProjectionMatrix();
      controls?.setAzimuthalAngle?.(base.current.azimut);
      debut.current = null;
    }
  });

  return null;
}

/** Intensités de la suspension selon le moment. Le repos vaut 1. */
const AMBIANCES: Readonly<Record<string, { readonly lampe: number; readonly fond: number }>> = {
  // Un rampeau est un duel : on baisse la salle et on ne garde que la table.
  sombre: { lampe: 0.55, fond: 0.35 },
  // Un 421 se fête : la suspension monte d'un cran, comme si on se penchait.
  fete: { lampe: 1.35, fond: 1.2 },
  fort: { lampe: 1.12, fond: 1 },
  info: { lampe: 1, fond: 1 },
};

function Comptoir({ ambiance }: { readonly ambiance: string | null }) {
  const reglage = AMBIANCES[ambiance ?? ""] ?? { lampe: 1, fond: 1 };
  return (
    <>
      {/* La suspension du bistrot : chaude, presque à l'aplomb du plateau. Elle
          n'a plus à porter les ombres — voir `OmbresAuSol` plus bas. */}
      <directionalLight
        position={[1.6, 11, 1.2]}
        intensity={1.95 * reglage.lampe}
        color="#ffd9a3"
      />
      {/* appoint froid, côté rue */}
      <directionalLight
        position={[-4, 2, -3]}
        intensity={0.35 * reglage.fond}
        color="#9fc4ff"
      />
      <ambientLight intensity={0.4 * reglage.fond} />
    </>
  );
}

/**
 * Les ombres des dés, projetées au sol.
 *
 * Une **ombre de contact**, et non une carte d'ombre portée par la suspension. Le
 * choix est né d'un défaut mesurable : avec la carte d'ombre, les trois ombres
 * apparaissaient toutes décalées du même vecteur, à deux unités des dés, quelle que
 * soit la position de la lumière — un décalage constant, donc étranger à l'éclairage.
 * Sur un plateau rond, cela se lisait comme des taches posées à côté du jeu.
 *
 * L'ombre de contact rend exactement ce qu'on veut ici : un dé posé sur un feutre a
 * une ombre courte, douce, et directement sous lui. Elle coûte un rendu de profondeur
 * et un seul appel de dessin, et `mesurer-perfs.py` surveille les deux.
 */
function OmbresAuSol() {
  return (
    <ContactShadows
      position={[0, PLATEAU.surface + 0.002, 0]}
      scale={PLATEAU.rayon * 2.2}
      resolution={512}
      /* Au-delà d'un dé et demi de haut, un dé en vol ne projette plus rien : son
         ombre s'efface pendant le lancer, comme dans la réalité. */
      far={1.6}
      blur={2.2}
      opacity={0.62}
      color="#04120c"
    />
  );
}

/** Piste affichée tant que la scène des dés (et son WASM) n'est pas chargée. */
function TapisSimple() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]} receiveShadow>
      <circleGeometry args={[4.4, 64]} />
      <meshStandardMaterial color="#0f5138" roughness={0.95} />
    </mesh>
  );
}

/**
 * Surveille la perte du contexte WebGL — exigence du § 7.3, et réalité mobile.
 *
 * Un contexte se perd quand l'OS récupère de la mémoire GPU, au retour
 * d'arrière-plan, ou quand le pilote redémarre. Sans `preventDefault()`, le
 * navigateur ne le restaure **jamais** : la scène reste noire pour de bon.
 *
 * On remonte ensuite le Canvas avec une clé neuve plutôt que d'espérer que trois
 * douzaines de ressources GPU se recréent d'elles-mêmes. Et c'est sans conséquence
 * pour le joueur, parce que l'état de la partie vit **hors** de la scène (§ 3.2) : la
 * partie n'est pas perdue, seul l'affichage est refait.
 */
function SurveillanceWebGL({ onPerdu, onRetrouve }: {
  readonly onPerdu: () => void;
  readonly onRetrouve: () => void;
}) {
  const gl = useThree((s) => s.gl);

  useEffect(() => {
    const toile = gl.domElement;
    const perdu = (evenement: Event) => {
      // Sans ceci, la restauration n'a simplement pas lieu.
      evenement.preventDefault();
      onPerdu();
    };
    toile.addEventListener("webglcontextlost", perdu);
    toile.addEventListener("webglcontextrestored", onRetrouve);
    return () => {
      toile.removeEventListener("webglcontextlost", perdu);
      toile.removeEventListener("webglcontextrestored", onRetrouve);
    };
  }, [gl, onPerdu, onRetrouve]);

  return null;
}

interface Props {
  /**
   * L'ambiance du moment, commandée par la dernière annonce.
   *
   * Le § 3.3 demande que le rampeau assombrisse le tapis et que le 421 se fête. Le
   * faire porter par la LUMIÈRE plutôt que par un décor ajouté suit la même règle
   * que le dé gardé : on change la matière, on n'empile pas des symboles.
   */
  readonly ambiance?: "info" | "fort" | "fete" | "sombre" | null;
  readonly des: readonly DeAffiche[];
  readonly poses: readonly DePose[];
  readonly demande: DemandeDeJet | null;
  readonly onJetJoue: (indices: readonly number[], jet: Jet) => void;
  readonly onDeClique: (index: number) => void;
  /**
   * Compteur de jets posés. À chaque incrément, la caméra se resserre brièvement sur
   * le résultat — demandé par Olivier le 2026-07-26, en renversant l'arbitrage
   * « aucun mouvement de caméra ». C'est un zoom, pas un déplacement : le point de
   * vue ne bouge pas, et c'est le déplacement qui donnait le tournis en portrait.
   */
  readonly regard?: number;
  /** Un dé vient de toucher, avec la force du choc (0 à 1). */
  readonly onImpact?: ((force: number) => void) | undefined;
  /** Prévient que le contexte 3D est perdu : au parent d'annuler ce qu'il attendait. */
  readonly onContextePerdu?: () => void;
}

export function Scene({
  ambiance, des, poses, demande, regard = 0, onJetJoue, onDeClique, onImpact,
  onContextePerdu,
}: Props) {
  const [qualite, setQualite] = useState<"haute" | "basse">("haute");
  /**
   * L'ampleur du regard : zéro quand le système demande moins de mouvement.
   *
   * On met le mouvement à ZÉRO plutôt que de retirer le composant — le reste du code
   * continue alors de tourner sans condition, donc sans second chemin à tester.
   */
  const [ampleur, setAmpleur] = useState(1);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const requete = matchMedia("(prefers-reduced-motion: reduce)");
    const suivre = () => { setAmpleur(requete.matches ? 0 : 1); };
    suivre();
    requete.addEventListener("change", suivre);
    return () => { requete.removeEventListener("change", suivre); };
  }, []);
  /** Incrémentée pour remonter la scène après une perte de contexte WebGL. */
  const [generation, setGeneration] = useState(0);
  const [contextePerdu, setContextePerdu] = useState(false);

  // Repli automatique : on abaisse le DPR avant de couper les ombres, car sur iOS
  // la résolution interne coûte plus cher que l'ombre elle-même.
  useEffect(() => {
    let images = 0;
    let debut = performance.now();
    let actif = true;
    const boucle = () => {
      if (!actif) return;
      images++;
      const ecoule = performance.now() - debut;
      if (ecoule > 3000) {
        if ((images * 1000) / ecoule < 45) setQualite("basse");
        images = 0;
        debut = performance.now();
      }
      requestAnimationFrame(boucle);
    };
    requestAnimationFrame(boucle);
    return () => { actif = false; };
  }, []);

  const surContextePerdu = useCallback(() => {
    setContextePerdu(true);
    onContextePerdu?.();
  }, [onContextePerdu]);

  const surContexteRetrouve = useCallback(() => {
    setContextePerdu(false);
    setGeneration((n) => n + 1);
  }, []);

  return (
    <>
      <Canvas
        key={generation}
        shadows={qualite === "haute"}
        dpr={qualite === "haute" ? [1, 2] : [1, 1.25]}
        /* 3/4 plongeante. Le champ est ensuite recalculé par CadrageAdaptatif selon
           la forme de l'écran ; les dés s'arrêtent dans une zone que `lancer.ts`
           garantit visible. */
        camera={{ position: [...VUE.position], fov: 46 }}
      >
        <color attach="background" args={["#0b1f18"]} />
        {/* Le brouillard doit rester DERRIÈRE le plateau. Réglé pour l'ancienne
            piste, il commençait à 12 unités : la caméra ayant reculé au-delà de 25
            pour cadrer le plateau rond, la scène entière disparaissait dans le noir.
            Aucune erreur, aucun avertissement — juste un écran vide. */}
        <fog attach="fog" args={["#0b1f18", 34, 90]} />
        <CadrageAdaptatif />
        <Regard jet={regard} ampleur={ampleur} />
        <SurveillanceWebGL onPerdu={surContextePerdu} onRetrouve={surContexteRetrouve} />
        {sondeActive() && <Sonde />}
        <Comptoir ambiance={ambiance ?? null} />
        <OmbresAuSol />
        <Suspense fallback={<TapisSimple />}>
          <DesQuiRoulent
            des={des}
            poses={poses}
            demande={demande}
            onJetJoue={onJetJoue}
            onDeClique={onDeClique}
            onImpact={onImpact}
          />
        </Suspense>
        <OrbitControls
          makeDefault
          enablePan={false}
          target={[...VUE.cible]}
          /* Les bornes doivent laisser passer le recul calculé par `cadrerPiste` :
             sur un portrait de téléphone, voir le plateau entier demande de
             s'éloigner à une vingtaine d'unités. Un plafond trop bas ne provoque
             aucune erreur — il ramène simplement la caméra, et on se retrouve le
             nez sur les dés sans comprendre pourquoi. */
          minDistance={6}
          maxDistance={34}
          maxPolarAngle={1.35}
        />
      </Canvas>

      {contextePerdu && (
        <p className="alerte" role="alert">
          L’affichage 3D a été interrompu par le système. La partie est intacte&nbsp;;
          la scène revient dès que possible.
        </p>
      )}

      {qualite === "basse" && <span className="lecteur-ecran">Qualité réduite (fps bas)</span>}
    </>
  );
}
